/**
 * Service für die Verwaltung von Portal Context-Informationen
 *
 * Verwaltet gemeinsamen Anwendungskontext und Properties, die über
 * alle im Portal laufenden Anwendungen synchronisiert werden. Unterstützt
 * sowohl well-known Properties (Shell, vendor-spezifisch) als auch benutzerdefinierte Properties.
 *
 * @class ContextInfoService
 */
import { Injectable, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable } from 'rxjs';
import { PortalMessageBroker } from './portal-message-broker';

/**
 * Well-known Shell Properties für Anwendungsmodus und Embedding-Status
 */
export enum WellKnownShellProperties {
  APP_MODE_NORMAL = 'shell:app-mode:normal',
  APP_MODE_POPUP = 'shell:app-mode:popup',
  APP_MODE_WIDGET = 'shell:app-mode:widget',
  EMBEDDED_EMBEDDED = 'shell:embedded:embedded',
  EMBEDDED_EMBEDDING = 'shell:embedded:embedding',
}

/**
 * Well-known Just Farming Vendor Properties
 */
export enum WellKnownJustFarmingProperties {
  ANALYTICS = 'vendor:de.just-farming:analytics',
  ERROR_REPORTING = 'vendor:de.just-farming:errorReporting',
}

/**
 * Union Type für alle well-known Property-Keys
 */
export type WellKnownProperties = WellKnownShellProperties | WellKnownJustFarmingProperties;

/**
 * Property-Key kann entweder ein String oder ein well-known Property Enum sein
 */
export type PropertyKey = string | WellKnownProperties;

/**
 * Basis-Typen für Property-Werte
 */
export type Property = boolean | string | number | undefined;

/**
 * Array von Property-Werten
 */
export type ArrayProperty = Property[];

/**
 * Context Info Property kann ein einzelner Wert oder ein Array sein
 */
export type ContextInfoProperty = Property | ArrayProperty;

/**
 * Context Information Objekt mit allen geteilten Properties
 */
export interface ContextInfo {
  properties: Map<PropertyKey, ContextInfoProperty>;
}

@Injectable({
  providedIn: 'root',
})
export class ContextInfoService {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _logger = inject(NGXLogger);
  private readonly _portalMessageBroker = inject(PortalMessageBroker);

  /**
   * BehaviorSubject mit aktuellem Context Info State
   */
  private readonly _contextInfo$ = new BehaviorSubject<ContextInfo>({
    properties: new Map(),
  });

  constructor() {
    // Registriere Context Info Callback beim Portal Message Broker
    this._portalMessageBroker.registerContextInfoCallback((contextInfo) => {
      this._contextInfo$.next(contextInfo);
      this._logger.debug('ContextInfoService: new context info', this._contextInfo$.value);
    });
  }

  /**
   * Gibt den aktuellen Context Info Snapshot zurück
   *
   * @returns {ContextInfo} Aktueller Context Info
   */
  get contextInfo(): ContextInfo {
    return this._contextInfo$.value;
  }

  /**
   * Gibt Observable-Stream von Context Info Updates zurück
   *
   * Unsubscribed automatisch wenn die Komponente zerstört wird.
   *
   * @returns {Observable<ContextInfo>} Observable mit Context Info Updates
   */
  get contextInfo$(): Observable<ContextInfo> {
    return this._contextInfo$.asObservable().pipe(takeUntilDestroyed(this._destroyRef));
  }

  /**
   * Sendet einen geteilten Property-Wert an das Portal
   *
   * Die Property wird anderen Anwendungen über ihr ContextInfo-Objekt
   * zur Verfügung gestellt. Funktioniert nur wenn innerhalb des Portals ausgeführt.
   *
   * @param {string} key Eindeutiger Property-Identifier
   * @param {ContextInfoProperty} value Zu teilender Property-Wert
   */
  public submitProperty(key: string, value: ContextInfoProperty): void {
    if (this._portalMessageBroker.isRunningInPortal()) {
      this._portalMessageBroker.submitContextInfoProperty(key, value).catch((error) => {
        this._logger.error('ContextInfoService: Failed to submit property', key, error);
      });
    }
  }
}
